import { useI18n } from '../../../i18n';
// ============================================
// PromotionFormModal - Add / Edit
// ============================================

import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useProducts, useCategories, useStores } from '../../../hooks';
import { useMoney } from '../../../providers/CurrencyProvider';
import { TagSearchInput, type TagOption } from './TagSearchInput';

export interface PromotionFormData {
  name: string;
  code: string;
  type: 'PERCENTAGE' | 'FIXED_AMOUNT';
  value: string;
  conditions: string;
  start_at: string;
  end_at: string;
  status: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'EXPIRED';
  /**
   * Bug #140: số điểm khách phải đổi để lấy voucher này (`points_cost`).
   *
   * App khách đã có sẵn luồng "đổi điểm lấy voucher" (`useRedeemPromotionPoints`
   * -> `POST /promotions/{id}/redeem/`) và màn chi tiết ưu đãi hiển thị số điểm.
   * Nhưng trang admin không có ô nào để nhập, nên `points_cost` không bao giờ
   * được đặt và app khách luôn hiện 0 — nhìn như voucher đổi được miễn phí.
   *
   * Chuỗi rỗng = không dùng điểm (voucher claim tự do), khác hẳn với 0 điểm.
   */
  points_cost: string;
  /**
   * Giới hạn TỔNG số lượt dùng của voucher này.
   *
   * ⚠️ Tên field gửi lên BE là **`total_quantity`**, không phải `usage_limit`
   * (bug #251, BE xác nhận 18/09/2026). Bản trước đặt tên form là `usage_limit`
   * và gửi nguyên tên đó — DRF bỏ qua field lạ trong im lặng nên request vẫn
   * 200 mà giá trị không bao giờ được lưu. Tên trong form giờ trùng đúng tên
   * BE, để không ai phải nhớ một bảng quy đổi nào nữa.
   *
   * Chuỗi rỗng = không giới hạn. Khác với 0 — 0 nghĩa là không ai dùng được.
   */
  total_quantity: string;
  product_ids: TagOption[];
  category_ids: TagOption[];
  store_ids: TagOption[];
}

export type PromotionFormErrors = Partial<Record<keyof PromotionFormData, string>>;

export const emptyPromotionForm: PromotionFormData = {
  name: '',
  code: '',
  type: 'PERCENTAGE',
  value: '',
  conditions: '',
  start_at: '',
  end_at: '',
  status: 'DRAFT',
  points_cost: '',
  total_quantity: '',
  product_ids: [],
  category_ids: [],
  store_ids: [],
};

/**
 * Bug #193: mọi thông báo lỗi ở đây trước đây là chuỗi tiếng Anh viết cứng, nên
 * đổi giao diện sang PL/UA rồi bấm Save với form trống thì nhãn trường đã dịch
 * mà thông báo lỗi bên dưới vẫn là tiếng Anh. Nhận `t` vào để đi qua i18n.
 */
function validateForm(
  form: PromotionFormData,
  t: (key: string) => string
): PromotionFormErrors {
  const errors: PromotionFormErrors = {};
  if (!form.name.trim()) errors.name = t('validationPromotionNameRequired');
  if (!form.code.trim()) errors.code = t('validationPromotionCodeRequired');
  if (!form.value.trim()) errors.value = t('validationValueRequired');
  else if (isNaN(parseFloat(form.value))) errors.value = t('validationMustBeValidNumber');
  if (form.points_cost.trim()) {
    const points = Number(form.points_cost);
    if (!Number.isFinite(points) || points < 0 || !Number.isInteger(points)) {
      errors.points_cost = t('validationPointsCostInvalid');
    }
  }
  if (form.total_quantity.trim()) {
    const limit = Number(form.total_quantity);
    // Tối thiểu 1, không phải 0: "giới hạn 0 lượt" là voucher chết, gần như
    // luôn là gõ nhầm. Muốn khoá thì đặt status INACTIVE.
    if (!Number.isFinite(limit) || !Number.isInteger(limit) || limit < 1) {
      errors.total_quantity = t('validationUsageLimitInvalid');
    }
  }
  if (!form.start_at) errors.start_at = t('validationStartDateRequired');
  if (!form.end_at) errors.end_at = t('validationEndDateRequired');
  if (form.start_at && form.end_at && new Date(form.end_at) < new Date(form.start_at))
    errors.end_at = t('validationEndDateAfterStartDate');
  return errors;
}

interface PromotionFormModalProps {
  mode: 'create' | 'edit';
  initialData: PromotionFormData;
  isSubmitting?: boolean;
  onSubmit: (data: PromotionFormData) => Promise<void>;
  onClose: () => void;
}

export function PromotionFormModal({
  mode,
  initialData,
  isSubmitting,
  onSubmit,
  onClose,
}: PromotionFormModalProps) {
  const { t } = useI18n();
  const money = useMoney();
  const [form, setForm] = useState<PromotionFormData>(initialData);
  const [errors, setErrors] = useState<PromotionFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const [productSearch, setProductSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('');

  const { data: productsData, isLoading: loadingProducts } = useProducts({ page_size: 100 });
  const { data: categoriesData, isLoading: loadingCategories } = useCategories({ limit: 100 });
  const { data: storesData, isLoading: loadingStores } = useStores({ page_size: 100 });

  const productOptions = useMemo<TagOption[]>(() => {
    const list: any[] = Array.isArray(productsData)
      ? productsData
      : (productsData as any)?.results ?? (productsData as any)?.items ?? (productsData as any)?.data ?? [];
    return list.map((p: any) => ({ id: String(p.id), label: String(p.name ?? p.id) }));
  }, [productsData]);

  const categoryOptions = useMemo<TagOption[]>(() => {
    const list: any[] = Array.isArray(categoriesData)
      ? categoriesData
      : (categoriesData as any)?.results ?? (categoriesData as any)?.items ?? (categoriesData as any)?.data ?? [];
    return list.map((c: any) => ({ id: String(c.id), label: String(c.name ?? c.id) }));
  }, [categoriesData]);

  const storeOptions = useMemo<TagOption[]>(() => {
    const list: any[] = Array.isArray(storesData)
      ? storesData
      : (storesData as any)?.results ?? (storesData as any)?.items ?? (storesData as any)?.data ?? [];
    return list.map((s: any) => ({ id: String(s.id), label: String(s.name ?? s.id) }));
  }, [storesData]);

  // Ở chế độ sửa, PromotionsPage chỉ có ID nên tag hiện ra là chuỗi UUID. Khi
  // danh sách options đã tải xong thì tra ngược ID -> tên để người dùng đọc được.
  const resolveTags = useCallback((tags: TagOption[], options: TagOption[]): TagOption[] => {
    if (options.length === 0) return tags;
    const labelById = new Map(options.map((opt) => [opt.id, opt.label]));
    return tags.map((tag) => ({ ...tag, label: labelById.get(tag.id) ?? tag.label }));
  }, []);

  const resolvedProductTags = useMemo(
    () => resolveTags(form.product_ids, productOptions),
    [form.product_ids, productOptions, resolveTags]
  );
  const resolvedCategoryTags = useMemo(
    () => resolveTags(form.category_ids, categoryOptions),
    [form.category_ids, categoryOptions, resolveTags]
  );
  const resolvedStoreTags = useMemo(
    () => resolveTags(form.store_ids, storeOptions),
    [form.store_ids, storeOptions, resolveTags]
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const set = useCallback(<K extends keyof PromotionFormData>(field: K, value: PromotionFormData[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    const errs = validateForm(form, t);
    setErrors(errs);
    if (Object.keys(errs).length > 0) { nameRef.current?.focus(); return; }
    try {
      await onSubmit(form);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('failedToSavePromotion'));
    }
  }, [form, onSubmit, t]);

  const inputCls = (field: keyof PromotionFormData) =>
    `h-10 w-full rounded-lg border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors[field] ? 'border-red-300' : 'border-slate-200'}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            {mode === 'create' ? t('addPromotion') : t('editPromotion')}
          </h3>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="border-t border-slate-100 px-6 py-5 space-y-4 overflow-y-auto overflow-x-hidden max-h-[70vh]">

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm text-slate-600">
                {t('name')} <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameRef}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. Summer Sale"
                className={inputCls('name')}
              />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm text-slate-600">
                {t('code')} <span className="text-red-500">*</span>
              </label>
              <input
                value={form.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                placeholder="e.g. SUMMER10"
                className={inputCls('code')}
              />
              {errors.code && <p className="mt-1 text-xs text-red-600">{errors.code}</p>}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm text-slate-600">
              {t('discount')} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
                <button
                  type="button"
                  onClick={() => set('type', 'PERCENTAGE')}
                  className={`px-3 h-10 text-sm font-medium transition-colors ${
                    form.type === 'PERCENTAGE' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  %
                </button>
                <button
                  type="button"
                  onClick={() => set('type', 'FIXED_AMOUNT')}
                  className={`px-3 h-10 text-sm font-medium transition-colors border-l border-slate-200 ${
                    form.type === 'FIXED_AMOUNT' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {/* Bug #129: nút ghi "$" nhưng ô nhập lại chú thích "VND" —
                      hai đơn vị tiền trong cùng một trường. Cả hai giờ lấy từ
                      utils/currency nên không thể lệch nhau nữa. */}
                  {money.symbol}
                </button>
              </div>
              <div className="relative flex-1">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.value}
                  onChange={(e) => set('value', e.target.value)}
                  placeholder={form.type === 'PERCENTAGE' ? 'e.g. 10' : 'e.g. 50'}
                  className={`h-10 w-full rounded-lg border px-3 pr-12 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.value ? 'border-red-300' : 'border-slate-200'}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
                  {/* Mã ISO phải cùng phạm vi với ký hiệu ở nút bên trái, nếu không
                      lại lệch đơn vị như bug #129. */}
                  {form.type === 'PERCENTAGE' ? '%' : money.currency}
                </span>
              </div>
            </div>
            {errors.value && <p className="mt-1 text-xs text-red-600">{errors.value}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm text-slate-600">{t('conditions')}</label>
            <input
              value={form.conditions}
              onChange={(e) => set('conditions', e.target.value)}
              placeholder="e.g. min order 100000"
              className={inputCls('conditions')}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-slate-600">
              {t('period')} <span className="text-red-500">*</span>
            </label>
            {/* datetime-local có min-width lớn — mobile xếp dọc để không tràn modal (bug #109) */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="datetime-local"
                value={form.start_at}
                onChange={(e) => set('start_at', e.target.value)}
                className={`h-10 w-full min-w-0 rounded-lg border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 sm:flex-1 ${errors.start_at ? 'border-red-300' : 'border-slate-200'}`}
              />
              <span className="shrink-0 text-center text-sm text-slate-400">{t('to') || 'to'}</span>
              <input
                type="datetime-local"
                value={form.end_at}
                onChange={(e) => set('end_at', e.target.value)}
                className={`h-10 w-full min-w-0 rounded-lg border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 sm:flex-1 ${errors.end_at ? 'border-red-300' : 'border-slate-200'}`}
              />
            </div>
            {(errors.start_at || errors.end_at) && (
              <p className="mt-1 text-xs text-red-600">{errors.start_at || errors.end_at}</p>
            )}
          </div>

          {/* Bug #140: điểm đổi voucher. Không bắt buộc — để trống nghĩa là
              khách claim tự do, không tốn điểm. */}
          <div>
            <label className="mb-1 block text-sm text-slate-600">{t('pointsCost')}</label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.points_cost}
              onChange={(e) => set('points_cost', e.target.value)}
              placeholder={t('pointsCostPlaceholder')}
              className={inputCls('points_cost')}
            />
            <p className="mt-1 text-xs text-slate-400">{t('pointsCostHint')}</p>
            {errors.points_cost && <p className="mt-1 text-xs text-red-600">{errors.points_cost}</p>}
          </div>

          {/* Giới hạn tổng lượt dùng. Để trống = không giới hạn — nói rõ bằng
              chữ, vì "không giới hạn" là mặc định tốn tiền nhất. */}
          <div>
            <label className="mb-1 block text-sm text-slate-600">{t('usageLimit')}</label>
            <input
              type="number"
              min="1"
              step="1"
              value={form.total_quantity}
              onChange={(e) => set('total_quantity', e.target.value)}
              placeholder={t('usageLimitPlaceholder')}
              className={inputCls('total_quantity')}
            />
            <p className="mt-1 text-xs text-slate-400">{t('usageLimitHint')}</p>
            {errors.total_quantity && <p className="mt-1 text-xs text-red-600">{errors.total_quantity}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm text-slate-600">{t('promotionsStatus')}</label>
            <select
              value={form.status}
              onChange={(e) => set('status', e.target.value as PromotionFormData['status'])}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <option value="DRAFT">{t('draft')}</option>
              <option value="ACTIVE">{t('promotionsActive')}</option>
              <option value="INACTIVE">{t('inactiveStatus') || 'Inactive'}</option>
              <option value="EXPIRED">{t('expired')}</option>
            </select>
          </div>

          {/* Bug #131: 3 mục phạm vi áp dụng trước đây chỉ hiện khi TẠO MỚI, nên
              mở sửa 1 promotion là không xem/đổi được Products/Categories/Stores.
              Sửa promotion cũng phải sửa được phạm vi — bỏ điều kiện mode. */}
          <TagSearchInput
            label={t('products')}
            tags={resolvedProductTags}
            options={productOptions}
            isLoading={loadingProducts}
            searchValue={productSearch}
            onSearchChange={setProductSearch}
            onAdd={(opt) => set('product_ids', [...form.product_ids, opt])}
            onRemove={(id) => set('product_ids', form.product_ids.filter((t) => t.id !== id))}
          />

          <TagSearchInput
            label={t('categories')}
            tags={resolvedCategoryTags}
            options={categoryOptions}
            isLoading={loadingCategories}
            searchValue={categorySearch}
            onSearchChange={setCategorySearch}
            onAdd={(opt) => set('category_ids', [...form.category_ids, opt])}
            onRemove={(id) => set('category_ids', form.category_ids.filter((t) => t.id !== id))}
          />

          <TagSearchInput
            label={t('stores')}
            tags={resolvedStoreTags}
            options={storeOptions}
            isLoading={loadingStores}
            searchValue={storeSearch}
            onSearchChange={setStoreSearch}
            onAdd={(opt) => set('store_ids', [...form.store_ids, opt])}
            onRemove={(id) => set('store_ids', form.store_ids.filter((t) => t.id !== id))}
          />

          {submitError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
          )}
        </div>

        <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {isSubmitting ? t('savingBtn') : t('saveBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
